import { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';

import { Route, Routes } from 'react-router-dom';

import {
    MantineProvider,
    createTheme,
    localStorageColorSchemeManager
} from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dropzone/styles.css';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';

import { AdminSettings } from './components/Admin/AdminSettings';
import DevOpsEditorView from './components/Editor/DevOpsEditorView.tsx';
import EditorView from './components/Editor/EditorView.tsx';
import HomeView from './components/Home/HomeView.tsx';
import { Layout } from './components/Layout';
import AxiosInterceptor from './components/auth/AxiosInterceptor.tsx';
import {
    IdentityContext,
    useIdentity
} from './components/auth/IdentityContext';
import { IdentityGuard } from './components/auth/IdentityGuard';
import { IdentityType } from './components/auth/IdentityType';
import { ProjectDataProvider } from './components/utils/ProjectDataProvider.tsx';
import {
    adminSettingsRoute,
    devOpsEditorRoute,
    editorRoute
} from './components/utils/RouteResolverUtils';

interface AppProps {
    msalInstance: IPublicClientApplication;
}

function App({ msalInstance }: AppProps) {
    const identity = useIdentity(msalInstance);

    const theme = createTheme({
        fontFamily: 'Manrope, sans-serif',
        headings: { fontFamily: 'Manrope, sans-serif' },
        colors: {
            brand: [
                '#fef2f2',
                '#fde8e8',
                '#fbd0d0',
                '#f8a9aa',
                '#f27577',
                '#E04045', // main shade (5)
                '#c4333a', // dark shade (6)
                '#a32a30',
                '#872428',
                '#6e2026'
            ],
            secondary: [
                '#f0fafa',
                '#d6f0f0',
                '#ade0e0',
                '#7dcbcb',
                '#50b5b5',
                '#3AABAB', // main shade (5)
                '#2f9494', // dark shade (6)
                '#267878',
                '#1f6060',
                '#194d4d'
            ],
            dark: [
                '#C8CAD0', // 0 - primary text
                '#8E919D', // 1 - secondary text
                '#6B6F7E', // 2 - muted text
                '#4E5260', // 3 - dim text
                '#2A2D36', // 4 - borders
                '#272A33', // 5 - dimmed surfaces
                '#232630', // 6 - card/paper bg
                '#1C1E24', // 7 - body bg
                '#16181D', // 8 - header/sidebar bg
                '#0f1114'  // 9 - deepest
            ]
        },
        primaryColor: 'brand',
        primaryShade: { light: 5, dark: 6 }
    });
    const colorSchemeManager = localStorageColorSchemeManager({
        key: 'color-mode'
    });

    return (
        <MantineProvider
            theme={theme}
            defaultColorScheme={'dark'}
            colorSchemeManager={colorSchemeManager}
        >
            <Notifications />
            <IdentityContext.Provider value={identity}>
                <MsalProvider instance={msalInstance}>
                    <AxiosInterceptor>
                        <ModalsProvider>
                            <ProjectDataProvider>
                                <div
                                    className='App'
                                    style={{ height: '100vh' }}
                                >
                                    <Pages />
                                </div>
                            </ProjectDataProvider>
                        </ModalsProvider>
                    </AxiosInterceptor>
                </MsalProvider>
            </IdentityContext.Provider>
        </MantineProvider>
    );
}

function Pages() {
    return (
        <Routes>
            <Route path='/' element={<Layout />}>
                <Route index element={<HomeView />} />
                <Route
                    path={editorRoute}
                    element={
                        <IdentityGuard
                            targets={[
                                IdentityType.User,
                                IdentityType.Developer,
                                IdentityType.Admin
                            ]}
                        >
                            <EditorView />
                        </IdentityGuard>
                    }
                />
                <Route
                    path={devOpsEditorRoute}
                    element={
                        <IdentityGuard targets={[IdentityType.Developer, IdentityType.Admin]}>
                            <DevOpsEditorView />
                        </IdentityGuard>
                    }
                />
                <Route
                    path={adminSettingsRoute}
                    element={
                        <IdentityGuard targets={[IdentityType.Admin]}>
                            <AdminSettings />
                        </IdentityGuard>
                    }
                />
            </Route>
        </Routes>
    );
}

export default App;
